import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Alert } from '../components/alert/alert';
import { Breadcrumbs } from '../components/breadcrumbs/breadcrumbs';
import { Button } from '../components/button/button';
import { LotCard } from '../components/card/lot-card';
import { Container } from '../components/container/container';
import { Image } from '../components/image/image';
import { LotNav } from '../components/nav/lot-nav';
import { TabBar, TabItem } from '../components/tab-bar/tab-bar';
import { LotCardContainer } from '../containers/lot-card-container';
import { useMeQuery, useMyLotsQuery } from '../graphql/types';
import useActiveYear from '../hooks/useActiveYear';

const Items = styled.div`
  margin-bottom: 32px;

  > a {
    display: block;
    margin-bottom: 16px;
  }

  > div {
    margin-bottom: 16px;
  }
`;

export function MyLots() {
  const me = useMeQuery({
    fetchPolicy: 'cache-first',
  });
  const paddleId = me.data?.me?.paddle?.id;
  const lots = useMyLotsQuery({
    variables: { filter: { paddleId } },
    skip: !paddleId,
    fetchPolicy: 'network-only',
  });

  const ACTIVE_YEAR = useActiveYear();

  const auction = me.data?.activeAuction;
  const isLoading = lots.loading || me.loading;
  const biddedLots = lots.data?.allLots?.edges || [];

  const allItems = biddedLots.reduce<any>((acc, lot) => {
    return acc.concat(lot.node.yourBids.map((bid) => ({ ...bid, lot })));
  }, []);

  const leaderboardItems = biddedLots.reduce<any>((acc, lot) => {
    const yourBidIds = lot.node.yourBids.map((b) => b.id);
    return acc.concat(
      lot.node.highestBids
        .filter((bid) => yourBidIds.includes(bid.id))
        .map((bid) => ({ ...bid, lot }))
    );
  }, []);

  const outbidItems = biddedLots.reduce<any>((acc, lot) => {
    const highestBidIds = lot.node.highestBids.map((b) => b.id);
    return acc.concat(
      lot.node.yourBids
        .filter((bid) => !highestBidIds.includes(bid.id))
        .map((bid) => ({ ...bid, lot }))
    );
  }, []);

  const [filter, setFilter] = useState<'all' | 'outbid' | 'leaderboard'>('all');
  const visibleItems =
    filter === 'outbid'
      ? outbidItems
      : filter === 'leaderboard'
      ? leaderboardItems
      : allItems;

  return (
    <Container>
      <LotNav
        left={
          <Breadcrumbs>
            <p>My lots</p>
          </Breadcrumbs>
        }
        right={
          <Button
            variant="secondary"
            size="sm"
            as={Link}
            to={`/${ACTIVE_YEAR}/lots`}
          >
            Back
          </Button>
        }
      />
      <TabBar>
        <TabItem onClick={() => setFilter('all')} active={filter === 'all'}>
          All Bids ({allItems.length})
        </TabItem>
        <TabItem
          onClick={() => setFilter('leaderboard')}
          active={filter === 'leaderboard'}
        >
          On leaderboard ({leaderboardItems.length})
        </TabItem>
        <TabItem
          onClick={() => setFilter('outbid')}
          active={filter === 'outbid'}
        >
          Outbid ({outbidItems.length})
        </TabItem>
      </TabBar>
      <br />
      <Items>
        {visibleItems.map((bid: any) => (
          <LotCardContainer
            key={bid.id}
            lot={bid.lot.node}
            auction={auction ?? undefined}
            yourbid={bid}
          />
        ))}
        {isLoading &&
          [...Array(20)].map((_, i) => (
            <LotCard
              key={i}
              isLoading
              image={<Image src="/temp/image_example_tall_2.png" alt="" />}
              brandName="213.  Blackbird"
              title="2019 Blackbird Arise"
              highestBid={1200}
              summary="2019 Blackbird"
            />
          ))}
        {!isLoading && (
          <>
            {filter === 'all' && allItems.length === 0 && (
              <Alert copy="You haven’t placed any bids in this auction. Once you’ve placed a bid on a lot it will be reflected on this page." />
            )}
            {filter === 'leaderboard' && leaderboardItems.length === 0 && (
              <Alert copy="Once you bid on a lot and are on the leaderboard, it will be reflected on this page." />
            )}
            {filter === 'outbid' && outbidItems.length === 0 && (
              <Alert copy="Once you have been outbid on a lot and are not on the leaderboard, it will be reflected on this page." />
            )}
          </>
        )}
      </Items>
    </Container>
  );
}
